import { type PropsWithChildren, type FunctionComponent, useEffect, useState, useMemo } from 'react'
import { matchPath } from 'react-router'
import { routes } from '@retailer-platform/domain-instacart-developer-platform'
import ErrorBoundary from '../../../legacy/components/ErrorBoundary'
import FeatureEducationModal from '../../../legacy/components/FeatureHighlights/FeatureEducationModal'
import useFeatureEducation from '../../../legacy/components/FeatureHighlights/useFeatureEducation'
import { NavV3OptOutSurveyProvider } from '../../../gin-and-tonic/common/retailer-satisfaction-survey/nav-v3-opt-out/NavV3OptOutSurveyProvider'
import { NavV3OnboardingTourProvider } from '../../../gin-and-tonic/common/nav-v3-onboarding/NavV3OnboardingTourProvider'
import { NavVersionDailyTracker } from '../../../gin-and-tonic/containers/nav-v3/NavVersionDailyTracker'
import { AppContentContainer } from '../../../gin-and-tonic/containers/app-content-container/AppContentContainer'
import { UserFeedbackButton } from '../../../gin-and-tonic/containers/user-feedback/UserFeedbackButton'
import { LeftShortcutsPaneRetailer } from '../../../gin-and-tonic/containers/nav-v2/components/LeftShortcutsPane/LeftShortcutsPaneRetailer'
import { MenuBarTop } from '../../../gin-and-tonic/containers/nav-v2/components/MenuBarTop'
import { useNavHierarchyRetailer } from '../../../gin-and-tonic/containers/nav-v2/NavMenuHierarchy'
import {
  TdsNavigationBar,
  TOPBAR_HEIGHT,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from '../../../gin-and-tonic/containers/nav-v3/TdsNavigationBar'
import { useNavHierarchyRetailerV3 } from '../../../gin-and-tonic/containers/nav-v3/useNavHierarchyRetailerDataV3'
import { useSidebarCollapsedPersistence } from '../../../gin-and-tonic/containers/nav-v3/useSidebarCollapsedPersistence'
import { TdsSettingsSidebar } from '../../../gin-and-tonic/containers/nav-v3/TdsSettingsSidebar'
import { useIsOnSettingsRoute } from '../../../gin-and-tonic/containers/nav-v3/useIsOnSettingsRoute'
import { useWindowSizeIsWide } from '../../../utils/window/windows.hooks'
import { ContactHeader } from '../../utils/ContactHeader'
import { DASH_NAV_V2_HEIGHT } from '../../../legacy/common/theme'
import { useNavContext } from '../../../gin-and-tonic/containers/nav/utils/NavContext'
import { DashRoute } from '../../../legacy/dash-components/dash-route/DashRoute'
import { DashSwitch } from '../../../legacy/dash-components/dash-switch/DashSwitch'
import { DashRedirect } from '../../../legacy/dash-components/dash-redirect/DashRedirect'
import { HaltTerms } from '../../../sections/halt/components/terms/HaltTerms'
import { usePartnerContext, URL_PARTNER_HALT_TERMS } from '../../../exports/routing'
import { url } from '../../../utils/parsing/url'
import { AgreementTypeEnum } from '../../../__codegen__/api'
import { useCurrentAccountContext } from '../../../utils/current-account/CurrentAccountContext'
import { useImpersonationBannerHeight } from '../../../gin-and-tonic/containers/impersonation-status-banner/ImpersonationStatusBanner'
import { useNavV3Gate } from '../../../gin-and-tonic/containers/nav-v3/useNavV3Gate'
import { AttachPartnerAnalyticsMetadata } from '../../utils/AttachAnalyticsMetadata'
import { PartnerType } from '../../../exports/utils'
import {
  consumeStoredRedirectUrl,
  storeCurrentRedirectUrl,
  TERMS_REDIRECT_KEY,
} from '../utils/loggedIn.utils'
import { URL_DASHBOARD } from '../../../utils/routing/routes'
import { AiPageLayout } from '../../../utils/contexts/ai/AiPageLayout'
import { RetailerAgentSessionContextProvider } from '../../../modules/retailer-agent/RetailerAgentPageContext'
import { RetailerAgentOverlay } from '../../../modules/retailer-agent/RetailerAgentOverlay'
import { RetailerScopedSection } from './retailer-scoped/RetailerScopedSection'
import { PartnerScopedRoutes } from './PartnerScopedRoutes'

// Single source of truth for whether the partner-scoped UI renders the new
// TDS NavigationBar (v3) or the legacy MenuBarTop (v2). Both the layout shell
// (PartnerScopedApp) and the nav component (PartnerScopedNavBar) read this so
// the two stay in sync. `useNavV3Gate().enabled` folds the three independent
// conditions (rt_ipp_nav_v3, R2026_05_0, and the `useTdsNav` admin control)
// into a single boolean — see useNavV3Gate.ts.
const useTdsNav = () => useNavV3Gate().enabled

/**
 * v2 nav bar wrapper. Owns `useNavHierarchyRetailer()` so its context
 * subscriptions don't ripple into PartnerScopedApp.
 */
const PartnerScopedNavBarV2: FunctionComponent = () => {
  const { navMenuHierarchy } = useNavHierarchyRetailer()
  return <MenuBarTop navMenuHierarchy={navMenuHierarchy} />
}

/**
 * v3 nav bar wrapper. Owns `useNavHierarchyRetailerV3()` (separate hook from v2;
 * the v3 sidebar uses an independent L1 list — see useNavHierarchyRetailerDataV3.ts).
 */
const PartnerScopedNavBarV3: FunctionComponent<{
  isCollapsed: boolean
  setIsCollapsed: (next: boolean) => void
  hideSidebar: boolean
}> = ({ isCollapsed, setIsCollapsed, hideSidebar }) => {
  const { navMenuHierarchy } = useNavHierarchyRetailerV3()
  return (
    <TdsNavigationBar
      navMenuHierarchy={navMenuHierarchy}
      isCollapsed={isCollapsed}
      onCollapsedChange={setIsCollapsed}
      homeRoute="dashboard"
      hideSidebar={hideSidebar}
    />
  )
}

const PartnerScopedNavBar: FunctionComponent<{
  isNavVisible: boolean
  isCollapsed: boolean
  setIsCollapsed: (next: boolean) => void
  hideSidebar: boolean
}> = ({ isNavVisible, isCollapsed, setIsCollapsed, hideSidebar }) => {
  const tdsNav = useTdsNav()

  if (!isNavVisible) return null

  return tdsNav ? (
    <PartnerScopedNavBarV3
      isCollapsed={isCollapsed}
      setIsCollapsed={setIsCollapsed}
      hideSidebar={hideSidebar}
    />
  ) : (
    <PartnerScopedNavBarV2 />
  )
}

/**
 * Isolated component for feature education modals.
 * Owns useFeatureEducation() so its context subscriptions
 * (ICPlatformContext, ReactQueryContext, etc.) don't
 * cause PartnerScopedApp to re-render.
 */
const PartnerScopedFeatureEducation: FunctionComponent<{ pathname: string }> = ({ pathname }) => {
  const { isOpen, onOpen, onClose, onClick, feature } = useFeatureEducation(pathname)

  useEffect(() => {
    if (isOpen) {
      onOpen()
    }
  }, [isOpen, onOpen, pathname])

  return (
    // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
    <FeatureEducationModal isOpen={isOpen} onClose={onClose} onClick={onClick} feature={feature} />
  )
}

interface Props {
  pathname: string
}

export const PartnerScopedApp: FunctionComponent<PropsWithChildren<Props>> = ({ pathname }) => {
  const isWide = useWindowSizeIsWide()
  const { isNavVisible, isSideNavVisible } = useNavContext()
  const partnerContext = usePartnerContext()
  const tdsNav = useNavV3Gate().enabled

  const { account, refetch } = useCurrentAccountContext()
  const bannerHeight = useImpersonationBannerHeight()
  const [redirectUrl] = useState<string | null>(() => consumeStoredRedirectUrl(TERMS_REDIRECT_KEY))
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useSidebarCollapsedPersistence()
  const isOnSettingsRoute = useIsOnSettingsRoute()
  const currentSidebarWidth = isOnSettingsRoute
    ? 0
    : isSidebarCollapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : SIDEBAR_WIDTH

  // temporarily gate behind roulette
  const showIdpTerms = !!account.featureToggles?.find(feature => feature.name === 'rt_idp_terms')

  const showDashboardTerms = !!account.featureToggles?.find(
    feature => feature.name === 'rt_dashboard_terms'
  )

  const expectedPartnerAgreementType =
    partnerContext.partnerType === PartnerType.IDP
      ? AgreementTypeEnum.IdpV1
      : AgreementTypeEnum.PartnerDashboardV1

  const termsAccepted = useMemo(
    () =>
      account.acceptedPartnerAgreements.some(
        agreement => agreement.agreementType === expectedPartnerAgreementType
      ),
    [account.acceptedPartnerAgreements, expectedPartnerAgreementType]
  )

  const isImpersonationSession = account.isImpersonating

  const shouldRedirectToTerms = useMemo(
    () =>
      ((showIdpTerms && partnerContext.partnerType === PartnerType.IDP) ||
        (showDashboardTerms && partnerContext.partnerType === PartnerType.Retailer)) &&
      !termsAccepted &&
      !isImpersonationSession &&
      !matchPath(pathname, { path: URL_PARTNER_HALT_TERMS }),
    [
      showIdpTerms,
      showDashboardTerms,
      partnerContext.partnerType,
      termsAccepted,
      pathname,
      isImpersonationSession,
    ]
  )

  const shouldConsumeTermsRedirectUrl = useMemo(
    () => matchPath(pathname, { path: URL_PARTNER_HALT_TERMS }) && termsAccepted,
    [pathname, termsAccepted]
  )

  // Wait for partner data before rendering — inlined from useRequiredNavDataLoadingStatus()
  // to avoid an extra PartnerContext subscription from that hook
  if (!partnerContext.partner) {
    return null
  }

  if (shouldRedirectToTerms) {
    storeCurrentRedirectUrl(TERMS_REDIRECT_KEY)
    return (
      <DashRedirect
        to={`${url(URL_PARTNER_HALT_TERMS, {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
          partner_id: partnerContext.partnerId,
        })}`}
      />
    )
  }

  if (shouldConsumeTermsRedirectUrl) {
    const defaultUrl =
      partnerContext.partnerType === PartnerType.IDP
        ? url(routes['instacart-developer-platform-get-started'], {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
            partner_id: partnerContext.partnerId,
          })
        : url(URL_DASHBOARD, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
            partner_id: partnerContext.partnerId,
          })

    return <DashRedirect to={redirectUrl || defaultUrl} />
  }

  const topPadding = isNavVisible ? DASH_NAV_V2_HEIGHT : '0px'
  const leftPadding = '0px'

  return (
    <RetailerAgentSessionContextProvider>
      <RetailerAgentOverlay />
      <NavV3OptOutSurveyProvider>
        <NavV3OnboardingTourProvider>
          <NavVersionDailyTracker />
          <DashSwitch>
            <DashRoute
              route="partner-halt-terms"
              render={() => <HaltTerms refetchCurrentAccount={refetch} />}
              exact
            />
            <DashRoute
              route="partner-scoped"
              render={() =>
                tdsNav ? (
                  // v3 shell: TDS NavigationBar needs a full-viewport flex column parent so its
                  // internal Flex.Column (TopBar 64px + Sidebar 273px wide, flexGrow:1) can lay
                  // out the L-shape across the viewport. Content is positioned over the empty
                  // top-right region using TDS constants for offsets.
                  <div
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: `calc(100vh - ${bannerHeight}px)`,
                      width: '100vw',
                      position: 'relative',
                    }}
                  >
                    <PartnerScopedNavBar
                      isNavVisible={isNavVisible}
                      isCollapsed={isSidebarCollapsed}
                      setIsCollapsed={setIsSidebarCollapsed}
                      hideSidebar={isOnSettingsRoute}
                    />
                    <AttachPartnerAnalyticsMetadata />
                    <UserFeedbackButton />
                    <PartnerScopedFeatureEducation pathname={pathname} />
                    <div
                      css={{
                        position: 'absolute',
                        top: isNavVisible ? TOPBAR_HEIGHT : 0,
                        left: isNavVisible ? currentSidebarWidth : 0,
                        width: isNavVisible ? `calc(100% - ${currentSidebarWidth}px)` : '100%',
                        height: isNavVisible ? `calc(100% - ${TOPBAR_HEIGHT}px)` : '100%',
                        display: 'flex',
                      }}
                    >
                      <TdsSettingsSidebar homeRoute="dashboard" />
                      <AiPageLayout>
                        <AppContentContainer css={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                          <ContactHeader />
                          <ErrorBoundary pathname={pathname}>
                            <DashSwitch>
                              <DashRoute
                                route="retailer-scoped"
                                component={RetailerScopedSection}
                              />
                              <PartnerScopedRoutes />
                            </DashSwitch>
                          </ErrorBoundary>
                        </AppContentContainer>
                      </AiPageLayout>
                    </div>
                  </div>
                ) : (
                  <>
                    <PartnerScopedNavBar
                      isNavVisible={isNavVisible}
                      isCollapsed={isSidebarCollapsed}
                      setIsCollapsed={setIsSidebarCollapsed}
                      hideSidebar={isOnSettingsRoute}
                    />
                    <AttachPartnerAnalyticsMetadata />
                    <UserFeedbackButton />
                    <div
                      css={{
                        display: 'flex',

                        // this CSS is a relic of the Nav V1 implementation. If we don't position absolute left: 0
                        // the content is shifted off screen to the left.
                        position: 'absolute',
                        left: leftPadding,

                        width: `calc(100% - ${leftPadding})`,
                        minHeight: `calc(100% - ${topPadding})`,
                        height: `calc(100% - ${topPadding})`,
                      }}
                    >
                      {isNavVisible && isSideNavVisible && isWide && <LeftShortcutsPaneRetailer />}

                      <PartnerScopedFeatureEducation pathname={pathname} />

                      <AiPageLayout>
                        <AppContentContainer css={{ flex: 1, minWidth: 0 }}>
                          <ContactHeader />
                          <ErrorBoundary pathname={pathname}>
                            <DashSwitch>
                              <DashRoute
                                route="retailer-scoped"
                                component={RetailerScopedSection}
                              />
                              <PartnerScopedRoutes />
                            </DashSwitch>
                          </ErrorBoundary>
                        </AppContentContainer>
                      </AiPageLayout>
                    </div>
                  </>
                )
              }
            />
          </DashSwitch>
        </NavV3OnboardingTourProvider>
      </NavV3OptOutSurveyProvider>
    </RetailerAgentSessionContextProvider>
  )
}
